// lib/store/slice/locationSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import API_PATH from "~/utils/api-routes";
import { createSmkApi } from "~/utils/smkInterseptor";

interface LocationItem {
  id: number;
  name: string;
}

interface LocationState {
  countryData: LocationItem[];
  statesData: LocationItem[];
  districtsData: LocationItem[];
  talukasData: LocationItem[];
  villagesData: LocationItem[];
  loading: boolean;
  error: string | null;
}

const initialState: LocationState = {
  countryData: [],
  statesData: [],
  districtsData: [],
  talukasData: [],
  villagesData: [],
  loading: false,
  error: null,
};

const smkApi = createSmkApi();

// Async thunks
export const getCountriesThunk = createAsyncThunk("location/getCountries", async (_, { rejectWithValue }) => {
  try {
    const response = await smkApi.get(API_PATH.LOCATION_COUNTRY);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    const message = err.response?.data?.message || err.message || "An error occurred while fetching countries";
    return rejectWithValue(message);
  }
});

export const getStatesThunk = createAsyncThunk("location/getStates", async ({ countryId }: { countryId: number }, { rejectWithValue }) => {
  try {
    const response = await smkApi.get(`${API_PATH.LOCATION_STATE}/${countryId}`);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    const message = err.response?.data?.message || err.message || "An error occurred while fetching states";
    return rejectWithValue(message);
  }
});

export const getDistrictsThunk = createAsyncThunk("location/getDistricts", async ({ stateId }: { stateId: number }, { rejectWithValue }) => {
  try {
    const response = await smkApi.get(`${API_PATH.LOCATION_DISTRICT}/${stateId}`);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    const message = err.response?.data?.message || err.message || "An error occurred while fetching districts";
    return rejectWithValue(message);
  }
});

export const getTalukasThunk = createAsyncThunk("location/getTalukas", async ({ districtId }: { districtId: number }, { rejectWithValue }) => {
  try {
    const response = await smkApi.get(`${API_PATH.LOCATION_TALUKO}/${districtId}`);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    const message = err.response?.data?.message || err.message || "An error occurred while fetching talukas";
    return rejectWithValue(message);
  }
});

export const getVillagesThunk = createAsyncThunk("location/getVillages", async ({ talukaId }: { talukaId: number }, { rejectWithValue }) => {
  try {
    const response = await smkApi.get(`${API_PATH.LOCATION_VILLAGE}/${talukaId}`);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    const message = err.response?.data?.message || err.message || "An error occurred while fetching villages";
    return rejectWithValue(message);
  }
});

const locationSlice = createSlice({
  name: "location",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Countries
      .addCase(getCountriesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCountriesThunk.fulfilled, (state, action: PayloadAction<LocationItem[]>) => {
        state.loading = false;
        state.countryData = action.payload;
      })
      .addCase(getCountriesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to fetch countries";
      })

      // States
      .addCase(getStatesThunk.fulfilled, (state, action: PayloadAction<LocationItem[]>) => {
        state.statesData = action.payload;
      })

      // Districts
      .addCase(getDistrictsThunk.fulfilled, (state, action: PayloadAction<LocationItem[]>) => {
        state.districtsData = action.payload;
      })

      // Talukas
      .addCase(getTalukasThunk.fulfilled, (state, action: PayloadAction<LocationItem[]>) => {
        state.talukasData = action.payload;
      })

      // Villages
      .addCase(getVillagesThunk.fulfilled, (state, action: PayloadAction<LocationItem[]>) => {
        state.villagesData = action.payload;
      });
  },
});

export default locationSlice.reducer;
